import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Barcode, Copy, Download, RefreshCw, Check } from "lucide-react";

// The GS1 country prefix that identifies barcodes originated in Brazil.
// GS1 also assigns 790 to Brazil, but 789 is the historic default and what
// most Brazilian ERPs (including Bling) use by default.
const DEFAULT_PREFIX = "789";
const MAX_BATCH = 500;

export default function EanGenerator() {
  const [quantity, setQuantity] = useState(10);
  const [prefix, setPrefix] = useState(DEFAULT_PREFIX);
  const [codes, setCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  async function generate() {
    const q = Math.max(1, Math.min(MAX_BATCH, Number(quantity) || 1));
    setBusy(true);
    try {
      const { data } = await api.post("/ean/generate", { quantity: q, prefix });
      setCodes(data.codes || []);
      toast.success(`${data.codes.length} código(s) EAN-13 gerado(s).`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao gerar EAN.");
    } finally {
      setBusy(false);
    }
  }

  async function copyOne(code, idx) {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx((v) => (v === idx ? null : v)), 1500);
    } catch (_e) {
      toast.error("Não foi possível copiar.");
    }
  }

  async function copyAll() {
    if (codes.length === 0) return;
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success(`${codes.length} código(s) copiado(s).`);
    } catch (_e) {
      toast.error("Não foi possível copiar a lista.");
    }
  }

  function downloadCsv() {
    if (codes.length === 0) return;
    const header = "ean13\n";
    const body = codes.join("\n");
    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.download = `ean13-${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function downloadTxt() {
    if (codes.length === 0) return;
    const blob = new Blob([codes.join("\n") + "\n"], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    a.download = `ean13-${ts}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6" data-testid="ean-generator-page">
      <div className="border border-slate-200 bg-white p-6">
        <div className="flex items-start gap-3">
          <div className="mt-1 rounded bg-blue-50 p-2">
            <Barcode className="h-5 w-5 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Gerador de EAN
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Gere códigos EAN-13 válidos com o dígito verificador GS1 já calculado.
              O prefixo padrão <span className="font-mono font-semibold">789</span> identifica
              produtos originados no Brasil.
            </p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
              Prefixo
            </label>
            <input
              data-testid="ean-prefix-input"
              type="text"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.replace(/\D/g, "").slice(0, 12))}
              className="w-full border border-slate-300 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-blue-600"
              placeholder="789"
              inputMode="numeric"
            />
            <p className="mt-1 text-xs text-slate-500">
              Somente dígitos (2 a 12). Padrão Brasil: 789.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
              Quantidade
            </label>
            <input
              data-testid="ean-quantity-input"
              type="number"
              min={1}
              max={MAX_BATCH}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full border border-slate-300 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-blue-600"
            />
            <p className="mt-1 text-xs text-slate-500">1 a {MAX_BATCH} códigos por lote.</p>
          </div>

          <div className="flex items-end">
            <button
              data-testid="ean-generate-btn"
              onClick={generate}
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 bg-blue-700 px-4 py-2 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Gerando…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Gerar códigos
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {codes.length > 0 && (
        <div className="border border-slate-200 bg-white" data-testid="ean-results-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                {codes.length} código(s) gerados
              </h2>
              <p className="text-xs text-slate-500">
                Prefixo <span className="font-mono font-semibold">{prefix}</span> · dígito verificador GS1 calculado
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="ean-copy-all-btn"
                onClick={copyAll}
                className="flex items-center gap-2 border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-700 transition hover:border-slate-500"
              >
                <Copy className="h-3.5 w-3.5" />
                Copiar todos
              </button>
              <button
                data-testid="ean-download-csv-btn"
                onClick={downloadCsv}
                className="flex items-center gap-2 border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-700 transition hover:border-slate-500"
              >
                <Download className="h-3.5 w-3.5" />
                Baixar CSV
              </button>
              <button
                data-testid="ean-download-txt-btn"
                onClick={downloadTxt}
                className="flex items-center gap-2 border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-700 transition hover:border-slate-500"
              >
                <Download className="h-3.5 w-3.5" />
                Baixar TXT
              </button>
            </div>
          </div>

          <ul className="max-h-[520px] divide-y divide-slate-100 overflow-y-auto" data-testid="ean-codes-list">
            {codes.map((code, idx) => (
              <li
                key={idx}
                className="flex items-center justify-between px-6 py-2.5 transition hover:bg-slate-50"
                data-testid={`ean-code-row-${idx}`}
              >
                <div className="flex items-center gap-4">
                  <span className="w-10 text-right font-mono text-xs text-slate-400">
                    {idx + 1}
                  </span>
                  <span
                    className="font-mono text-base tracking-wider text-slate-900"
                    data-testid={`ean-code-value-${idx}`}
                  >
                    {code}
                  </span>
                </div>
                <button
                  data-testid={`ean-copy-btn-${idx}`}
                  onClick={() => copyOne(code, idx)}
                  className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500 transition hover:text-blue-700"
                  title="Copiar"
                >
                  {copiedIdx === idx ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="text-emerald-600">Copiado</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      Copiar
                    </>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
